import React, { useEffect, useRef } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import StaticPageLayout from '../../../components/layouts/StaticPageLayout';
import useHistory from '../../../hooks/useHistory';
import { encodeProjectsParams } from '../../Projects/constants';
import { decodeSharedProjectsParams, SHARED_PROJECT_TYPE_TABS } from './constants';
import { paths } from '../../../utils/constants/routes';
import GeneralLoader from '../../../components/loaders/GeneralLoader';
import GeneralError from '../../../components/errors/GeneralError';
import SharedProjectsList from './SharedProjectsList';
import FeedbackRequestView from './FeedbackRequestView';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import ProfilerFeedbackRequestView from '../../ProfilerTeam/desktop/ProfilerFeedbackRequestView';
import pubSub from '../../../utils/pubsub';
import { runInAction } from 'mobx';
import useElementHeightCssVar from '../../../hooks/useElementHeightCssvar';

const TeamDesktop = observer(() => {
  const { location, navigate } = useHistory();
  const { projectStore, profilerProjectStore, authStore, utilsStore } = useStore();
  const tabsRef = useRef(null);
  const state = useLocalObservable(() => ({
    isRendered: true,
    unrender: () => (state.isRendered = false),
    params: {},
    setParams: (params = {}) => {
      state.params = params;
    },
    get projectList() {
      if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING) {
        return projectStore.pendingSharedProjects
          .concat(profilerProjectStore.pendingSharedProjects)
          .slice()
          .sort(
            (a, b) =>
              b.sharedUsers.find((u) => u.id === authStore.currentUserId).dateReceived -
              a.sharedUsers.find((u) => u.id === authStore.currentUserId).dateReceived
          );
      } else {
        return projectStore.resolvedSharedProjects
          .concat(profilerProjectStore.resolvedSharedProjects)
          .slice()
          .sort(
            (a, b) =>
              b.sharedUsers.find((u) => u.id === authStore.currentUserId).dateResolved -
              a.sharedUsers.find((u) => u.id === authStore.currentUserId).dateResolved
          );
      }
    },
    get selectedProject() {
      return state.projectList.find((project) => project.id === state.params.selectedProjectId);
    },
    get showProjectsError() {
      return projectStore.loadProjectsError && !projectStore.allProjects.length;
    },
    get showProfilerProjectsError() {
      return profilerProjectStore.loadProjectsError && !profilerProjectStore.allProjects.length;
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    },
    savedProjectListScrollY: 0,
    setSavedProjectListScrollY: (height = 0) => {
      runInAction(() => {
        state.savedProjectListScrollY = height;
      });
    }
  }));

  useElementHeightCssVar('#root .tabs-wrap', '--team-feedback-projects-list-tabs-height');
  useElementHeightCssVar('#root .tabs.request-view', '--team-feedback-project-view-tabs-height');
  // The Profiler request view's sticky toolbar row, which replaced the in-grid
  // results header (TD-2733 measured that header on both routes for the heatmap calc).
  // Both routes render the same component under the same stylesheet, so both measure the
  // row; every use of the var carries a 0px fallback for the views without it.
  useElementHeightCssVar(
    '.page-team-feedback .team-feedback-toolbar',
    '--team-feedback-toolbar-height'
  );
  // The stacked heatmap's own chrome, scoped to this page: the hook writes to
  // document.documentElement and the Projects and Search pages measure the same names.
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-section-header',
    '--profiler-heatmap-section-header-height'
  );
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-controls-header.mobile-view',
    '--profiler-heatmap-controls-mobile-height'
  );
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-section .heatmap-legend',
    '--heatmap-legend-height'
  );

  useEffect(() => {
    return () => {
      state.unrender();
    };
  }, [state]);

  useEffect(() => {
    authStore.loadCompanyUsers();
  }, [authStore]);

  useEffect(() => {
    const savedProjectListHandler = (payload) => {
      if (payload.event === 'team-saved-project-list-scroll-y') {
        const _scrollY = window.scrollY;
        setTimeout(() => {
          state.setSavedProjectListScrollY(_scrollY);
        }, 100);
      }
    };

    pubSub.subscribe(savedProjectListHandler);

    return () => {
      pubSub.unsubscribe(savedProjectListHandler);
    };
  }, [state]);

  useEffect(() => {
    if (projectStore.isLoadingProjectsData || profilerProjectStore.isLoadingProjectsData) {
      return;
    }
    let [urlParams, params, shouldNavigate] = [null, {}, false];
    try {
      urlParams = new URLSearchParams(location.search);
      params = decodeSharedProjectsParams(urlParams.get('params'));
    } catch (ex) {
      params = { projectTypeTab: SHARED_PROJECT_TYPE_TABS.PENDING };
      shouldNavigate = true;
    }
    if (typeof params !== 'object' || params === null || params === undefined) {
      params = {};
    }

    if (!Object.values(SHARED_PROJECT_TYPE_TABS).includes(params.projectTypeTab)) {
      params.projectTypeTab = SHARED_PROJECT_TYPE_TABS.PENDING;
      shouldNavigate = true;
    }

    if (shouldNavigate) {
      navigate(paths.TEAM + '?params=' + encodeProjectsParams(params), { replace: true });
    }

    state.setParams(params);
  }, [
    state,
    projectStore,
    profilerProjectStore,
    projectStore.allProjects,
    profilerProjectStore.allProjects,
    projectStore.isLoadingProjectsData,
    profilerProjectStore.isLoadingProjectsData,
    location.search
  ]);

  const showError = state.showProjectsError && state.showProfilerProjectsError;

  const showProjectList =
    !showError &&
    Object.values(SHARED_PROJECT_TYPE_TABS).includes(state.params.projectTypeTab) &&
    !state.params.selectedProjectId;
  const showFeedbackRequest = !showError && state.params.selectedProjectId && state.selectedProject;

  const scrollMobileTabIntoView = (tabId = '') => {
    const tabDOM = document.getElementById(tabId);
    if (tabDOM) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          tabDOM.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 500);
      }, 100);
    }
  };

  const tabs = [
    {
      id: 'team_mobile_open_requests_tab',
      name: `Open requests (${
        projectStore.pendingSharedProjects.length +
        profilerProjectStore.pendingSharedProjects.length
      })`,
      onClick: () => {
        if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING) {
          return;
        }
        navigate(
          paths.TEAM +
            '?params=' +
            encodeProjectsParams({ projectTypeTab: SHARED_PROJECT_TYPE_TABS.PENDING })
        );
        scrollMobileTabIntoView('team_mobile_open_requests_tab');
      },
      isActive: state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
    },
    {
      id: 'team_mobile_resolved_requests_tab',
      name: `Resolved requests (${
        projectStore.resolvedSharedProjects.length +
        profilerProjectStore.resolvedSharedProjects.length
      })`,
      onClick: () => {
        if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED) {
          return;
        }
        navigate(
          paths.TEAM +
            '?params=' +
            encodeProjectsParams({ projectTypeTab: SHARED_PROJECT_TYPE_TABS.RESOLVED })
        );
        scrollMobileTabIntoView('team_mobile_resolved_requests_tab');
      },
      isActive: state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED
    }
  ];

  if (showError) {
    return (
      <StaticPageLayout page="team-feedback">
        <div className="team-projects-listing-error-group">
          <GeneralError
            errorMessage={projectStore.loadProjectsError}
            actionMessage="You may want to:"
            withHomePageButton={true}
            actionButtontext="Try again"
            onActionButtonClick={projectStore.loadProjects}
          />
          <GeneralError
            errorMessage={profilerProjectStore.loadProjectsError}
            actionMessage="You may want to:"
            withHomePageButton={true}
            actionButtontext="Try again"
            onActionButtonClick={profilerProjectStore.loadProjects}
            withLogoutButton
          />
        </div>
      </StaticPageLayout>
    );
  }

  if (
    projectStore.initialLoadProjects ||
    profilerProjectStore.initialLoadProjects ||
    ((projectStore.isLoadingProjects || profilerProjectStore.isLoadingProjects) &&
      !projectStore.allProjects.length &&
      !profilerProjectStore.allProjects.length)
  ) {
    return (
      <StaticPageLayout page="team-feedback">
        <GeneralLoader />
      </StaticPageLayout>
    );
  }

  if (showProjectList && state.savedProjectListScrollY !== 0) {
    const top = state.savedProjectListScrollY;
    state.setSavedProjectListScrollY(0);
    setTimeout(() => {
      window.scrollTo({ top });
    }, 0);
  }

  return (
    <StaticPageLayout page="team-feedback">
      {showProjectList && (
        <>
          <div className="tabs-wrap" ref={tabsRef}>
            <div className="tabs at-projects-listing">
              {tabs.map((tab) => {
                return (
                  <div
                    key={tab.name}
                    id={tab.id}
                    onClick={tab.onClick}
                    className={`tab ${tab.isActive && 'active'} noselect`}
                  >
                    {tab.name}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="table-responsive">
            <div className="projects-list-partial-errors">
              {(projectStore.isLoadingProjects || profilerProjectStore.isLoadingProjects) && (
                <GeneralLoader />
              )}
              {!!projectStore.loadProjectsError && !projectStore.allProjects.length && (
                <GeneralError
                  errorMessage={projectStore.loadProjectsError}
                  actionMessage="You may want to:"
                  actionButtontext="Try again"
                  onActionButtonClick={projectStore.loadProjects}
                />
              )}
              {!!profilerProjectStore.loadProjectsError &&
                !profilerProjectStore.allProjects.length && (
                  <GeneralError
                    errorMessage={profilerProjectStore.loadProjectsError}
                    actionMessage="You may want to:"
                    actionButtontext="Try again"
                    onActionButtonClick={profilerProjectStore.loadProjects}
                    withLogoutButton
                  />
                )}
            </div>
            {!!state.projectList.length && (
              <div className="projects-list-header">
                <div className="table-head">
                  <div className="item">Sender name</div>
                  <div className="item">Project name</div>
                  <div className="item">Profile</div>
                  <div className="item">Industries</div>
                  <div className="item">Results</div>
                  <div className="item">
                    {state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
                      ? 'Date received'
                      : 'Date resolved'}
                  </div>
                </div>
              </div>
            )}
            <SharedProjectsList projectList={state.projectList} params={state.params} />
          </div>
        </>
      )}
      {showFeedbackRequest &&
        (state.selectedProject.searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler ? (
          <ProfilerFeedbackRequestView
            project={state.selectedProject}
            params={state.params}
            isFromAgentView
          />
        ) : (
          <FeedbackRequestView project={state.selectedProject} params={state.params} />
        ))}
    </StaticPageLayout>
  );
});

export default TeamDesktop;
